import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import type { VitalElement, WizardState } from './types';

interface VitalElementsStepProps {
  state: WizardState;
  updateState: (patch: Partial<WizardState>) => void;
}

interface ElementCard {
  key: VitalElement;
  title: string;
  description: string;
}

const ELEMENT_CARDS: ElementCard[] = [
  { key: 'parentSku', title: 'Parent SKU', description: 'Style-level identifier' },
  { key: 'colorCode', title: 'Color Code', description: 'A unique colorway identifier' },
  { key: 'sizeCode', title: 'Size Code', description: 'A unique size identifier' },
];

const VitalElementsStep = ({ state, updateState }: VitalElementsStepProps) => {
  const toggleElement = useCallback(
    (element: VitalElement) => {
      const current = state.selectedVitalElements;
      const next = current.includes(element) ?
        current.filter(e => e !== element) :
        [...current, element];
      updateState({ selectedVitalElements: next });
    },
    [state.selectedVitalElements, updateState],
  );

  const selectedCount = state.selectedVitalElements.length;

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 1 }}>
        Which catalog vital elements should prices be set by?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select the catalog vital elements below that you&apos;d like to set price overrides by.
        This will inform how pricing is populated in the template based on your selections on the next screen.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {ELEMENT_CARDS.map((card) => {
          const isSelected = state.selectedVitalElements.includes(card.key);
          return (
            <Paper
              key={card.key}
              variant="outlined"
              onClick={() => toggleElement(card.key)}
              sx={{
                flex: 1,
                p: 2,
                cursor: 'pointer',
                borderColor: isSelected ? 'primary.main' : 'divider',
                borderWidth: isSelected ? 2 : 1,
                bgcolor: isSelected ? 'action.selected' : 'background.paper',
                transition: 'border-color 0.15s, background-color 0.15s',
                '&:hover': {
                  borderColor: isSelected ? 'primary.main' : 'text.secondary',
                },
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {card.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {card.description}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      <Typography variant="body2" color="text.secondary">
        {selectedCount === 0 ?
          'No vital elements selected' :
          `${selectedCount} vital element${selectedCount > 1 ? 's' : ''} selected`}
      </Typography>
    </Box>
  );
};

export default VitalElementsStep;
